import { createSlice } from '@reduxjs/toolkit'

const initialState = {
  // Basic passport information
  id: '',
  first_name: '',
  last_name: '',
  nationality: '',
  date_of_expiry: '',
  date_of_birth: '',
  date_of_issue: '',
  country: '',
  gender: '',
  passport_number: '',
  authority: '',
  save_passport_id: '',

  // Verification states
  verified: false,
  flag: false,
  send_secure_link: false,

  // OCR data
  ocr: {
    passport_number: { ocr: 0, score: '', manual: 0 },
    country: { ocr: 0, score: '', manual: 0 },
    first_name: { ocr: 0, score: '', manual: 0 },
    last_name: { ocr: 0, score: '', manual: 0 },
    date_of_birth: { ocr: 0, score: '', manual: 0 },
    date_of_expiry: { ocr: 0, score: '', manual: 0 },
    date_of_issue: { ocr: 0, score: '', manual: 0 },
    nationality: { ocr: 0, score: '', manual: 0 },
    authority: { ocr: 0, score: '', manual: 0 },
    gender: { ocr: 0, score: '', manual: 0 }
  },

  // Verification flags
  verify: {
    first_name: false,
    last_name: false,
    passport_number: false,
    country: false,
    date_of_expiry: false,
    date_of_birth: false,
    date_of_issue: false,
    nationality: false,
    authority: false,
    gender: false
  },

  // Files
  files: []
}

export const passportSlice = createSlice({
  name: 'passport',
  initialState,
  reducers: {
    // Basic passport data
    setPassport: (state, action) => {
      const newState = { ...state, ...action.payload }
      // Preserve existing files if not included in the payload
      if (!action.payload.files && state.files) {
        newState.files = state.files
      }
      return newState
    },
    updatePassportField: (state, action) => {
      const { field, value } = action.payload
      state[field] = value
    },
    initializePassport: () => initialState,

    // OCR data management
    setPassportOcr: (state, action) => {
      state.ocr = { ...state.ocr, ...action.payload }
    },
    updatePassportOcrField: (state, action) => {
      const { field, data } = action.payload
      state.ocr[field] = { ...state.ocr[field], ...data }
    },

    // Verification management
    setPassportVerify: (state, action) => {
      state.verify = { ...state.verify, ...action.payload }
    },
    updatePassportVerifyField: (state, action) => {
      const { field, value } = action.payload
      state.verify[field] = value
    },
    verifyAllPassportFields: (state, action) => {
      const { value } = action.payload
      Object.keys(state.verify).forEach(key => {
        state.verify[key] = value
      })
    },

    // File management
    addPassportFile: state => {
      state.files.push({})
    },
    setPassportFile: (state, action) => {
      const { file_no, file_obj, base64 } = action.payload
      state.files[file_no] = { file_obj, base64 }
    },
    setPassportFileID: (state, action) => {
      const { file_no, id, document_revision_id } = action.payload
      if (state.files[file_no]) {
        state.files[file_no].id = id
        if (document_revision_id) {
          state.files[file_no].file_obj.document_revision_id =
            document_revision_id
        }
      }
    },
    addPassportFileWithData: (state, action) => {
      if (state.files.length < 1) {
        state.files.push(action.payload.file)
      }
    },
    removePassportFile: (state, action) => {
      const { file_no } = action.payload
      state.files.splice(file_no, 1)
    },
    setAllPassportFiles: (state, action) => {
      state.files = action.payload.files
    },
    initializePassportFiles: state => {
      state.files = []
    },

    // Set passport from API
    setPassportFromAPI: (state, action) => {
      const newState = { ...state, ...action.payload }
      // Preserve existing files if not included in the API response
      if (!action.payload.files && state.files) {
        newState.files = state.files
      }
      return newState
    },
    setSavePassportIdFromApiResponse: (state, action) => {
      state.save_passport_id = action.payload
    },
   
  }
})

export const {
  setPassport,
  updatePassportField,
  initializePassport,
  setPassportOcr,
  updatePassportOcrField,
  setPassportVerify,
  updatePassportVerifyField,
  verifyAllPassportFields,
  addPassportFile,
  setPassportFile,
  setPassportFileID,
  addPassportFileWithData,
  removePassportFile,
  setAllPassportFiles,
  initializePassportFiles,
  setPassportFromAPI,
  setSavePassportIdFromApiResponse,
  setRestorePassportFileInDocumentsLibrary
} = passportSlice.actions

// Selectors
export const selectPassport = state => state?.passport
export const selectPassportBasicInfo = state => ({
  first_name: state.passport.first_name,
  last_name: state.passport.last_name,
  passport_number: state.passport.passport_number,
  nationality: state.passport.nationality,
  date_of_expiry: state.passport.date_of_expiry,
  date_of_birth: state.passport.date_of_birth
})
export const selectPassportFiles = state => state.passport.files
export const selectPassportVerification = state => state.passport.verify
export const selectPassportOcr = state => state.passport.ocr

export default passportSlice.reducer
